import styled, { css } from 'styled-components';
import { HTMLAttributes } from 'react';
import { MEDIA_QUERY } from '@optimus-web/breakpoints';
import {
    ANIMATION_CURVE,
    ANIMATION_DURATION,
    getSpacingToken,
    getTextTokenPreset,
    getToken,
    Sized,
    ZIndex,
} from '@optimus-web/common';
import {
    TransitionGroupChildBaseProps as DialogChildProps,
    StyledTransitionGroup,
    StyledTransitionGroupProps,
    TransitionState,
} from '@optimus-web/helpers';
import { Button, ButtonElement } from '../Button';
import { Stack, StackProps } from '../Stack';
import { Ellipticable, getEllipticableCss } from '../typography';
import { DialogPosition } from './types';

interface WithDialogPosition {
    position?: `${DialogPosition}`;
}

export const DialogContainerElement = styled(
    (
        props: Partial<StyledTransitionGroupProps<DialogChildProps>> & {
            height: number;
        } & HTMLAttributes<HTMLDivElement>
    ) => <StyledTransitionGroup {...props} />
)`
    ${({ height }) => css`
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        overflow: hidden;
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: ${ZIndex.High};
        pointer-events: none;
        height: ${height}px;
    `}
`;

export const BackdropElement = styled.div<DialogChildProps>`
    position: absolute;
    inset: 0;

    width: 100%;
    height: 100%;

    opacity: ${getToken('opacity0')};
    transition-duration: ${ANIMATION_DURATION.fast}ms;
    transition-timing-function: ${ANIMATION_CURVE.default};
    transition-property: opacity;

    pointer-events: initial;
    background-color: ${getToken('backgroundBackdrop')};

    ${({ transitionState, theme }) =>
        transitionState === TransitionState.ENTERED &&
        css`
            opacity: ${getToken('opacity1000')({ theme })};
            transition-duration: ${ANIMATION_DURATION.default}ms;
        `}
`;

enum DIALOG_SIZES {
    small = '468px',
    medium = '698px',
    large = '928px',
    extraLarge = large,
}

interface DialogElementProps extends DialogChildProps, Sized {
    position?: `${DialogPosition}`;
    noMaxWidth?: boolean;
    mobileFullscreen?: boolean;
}

export const DialogElement = styled.div<DialogElementProps>`
    background-color: ${getToken('backgroundStaticFloating')};
    border-radius: ${getToken('borderRadius200')}px;
    ${css`
        border: ${getToken('borderWidth100')}px solid ${getToken('borderStaticSecondary')};
    `};
    box-shadow: ${getToken('shadow300')};
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    z-index: 1;
    width: calc(100% - ${({ theme }) => getSpacingToken('200')({ theme }) * 2}px);
    max-height: calc(100% - ${({ theme }) => getSpacingToken('200')({ theme }) * 2}px);
    max-width: ${({ size = 'medium' }) => DIALOG_SIZES[size]};
    transition-duration: ${ANIMATION_DURATION.fast}ms;
    transition-timing-function: ${ANIMATION_CURVE.default};
    transition-property: opacity, transform;
    pointer-events: initial;
    padding-bottom: ${getSpacingToken('300')}px;
    opacity: ${getToken('opacity0')};
    transform: translateY(2em);
    box-sizing: border-box;

    ${({ position, theme }) =>
        position === DialogPosition.Corner &&
        css`
            position: absolute;
            right: ${getSpacingToken('300')({ theme })}px;
            bottom: ${getSpacingToken('300')({ theme })}px;
        `}
    ${({ transitionState, theme }) =>
        transitionState === TransitionState.ENTERED &&
        css`
            opacity: ${getToken('opacity1000')({ theme })};
            transform: translate(0) !important;
            transition-duration: ${ANIMATION_DURATION.default}ms;
        `}
        ${({ noMaxWidth, theme }) =>
        noMaxWidth
            ? css`
                  width: auto;
                  max-width: none;
              `
            : css`
                  ${MEDIA_QUERY.tabletUp} {
                      width: calc(100% - ${getToken('sizing400')({ theme }) * 2}px);
                      max-height: calc(100vh - ${getSpacingToken('300')({ theme }) * 2}px);
                  }
              `}
        ${({ mobileFullscreen }) =>
        mobileFullscreen &&
        css`
            ${mobileFullscreenCss}
        `};
`;

export const DialogHeader = styled(Stack)<StackProps & WithDialogPosition>`
    padding: ${getSpacingToken('300')}px;
    gap: ${getSpacingToken('200')}px;
`;

interface DialogTitleProps extends Ellipticable, WithDialogPosition {
    /**
     * Makes the h2 element grow to full width. Used by DialogBackendPage to show header actions in the title.
     */
    fullWidth?: boolean;
}

export const DialogTitle = styled.h2<DialogTitleProps>`
    margin: ${getSpacingToken('0')};
    color: ${getToken('textStaticPrimary')};
    ${getTextTokenPreset('titleMediumStrong')};
    ${getEllipticableCss}
    ${({ fullWidth }) =>
        fullWidth &&
        css`
            flex-grow: 1;
        `}
`;

export interface DialogContentElementProps {
    position?: `${DialogPosition}`;
    withoutPadding?: boolean;
}

export const DialogContentElement = styled.div<DialogContentElementProps>`
    padding: ${getSpacingToken('300')}px ${getSpacingToken('300')}px 0;
    ${DialogHeader} + & {
        padding-top: ${getSpacingToken('0')};
    }

    ${({ withoutPadding }) =>
        withoutPadding &&
        css`
            padding-left: ${getSpacingToken('0')};
            padding-right: ${getSpacingToken('0')};
        `}

    display: flex;

    overflow: auto;
    flex-direction: column;
    ${getTextTokenPreset('bodyLarge')};
    color: ${getToken('textStaticPrimary')};
`;

export const DialogFooterElement = styled.footer`
    padding: ${getSpacingToken('300')}px ${getSpacingToken('300')}px 0;
`;

export const DialogFooterBaseStack = styled(Stack).attrs({
    itemAlignment: 'center',
    spacing: '200',
})<{ responsive: boolean }>`
    ${ButtonElement} {
        ${MEDIA_QUERY.largePhoneMax} {
            width: 100%;
        }
    }
    ${({ responsive }) =>
        responsive &&
        css`
            ${MEDIA_QUERY.largePhoneMax} {
                width: 100%;
            }
        `};
`;

export const DialogFooterPrimaryStack = styled(DialogFooterBaseStack)<{ responsive: boolean }>`
    ${({ responsive }) =>
        responsive &&
        css`
            ${MEDIA_QUERY.largePhoneMax} {
                order: -1;
                flex-direction: column-reverse;
            }
        `}
`;

const mobileFullscreenCss = css`
    ${MEDIA_QUERY.largePhoneMax} {
        top: 0;
        right: 0;
        width: 100%;
        height: 100%;
        max-height: 100%;
        max-width: 100%;
        border-radius: ${getToken('borderRadius0')};

        ${DialogContentElement} {
            flex: 1;
        }
    }
`;

/*
 * TODO remove all of this once the migration is completed
 * see https://mews.atlassian.net/browse/DX-363
 */

export const DialogButtonElement = styled(Button)`
    &:not(:last-child) {
        ${MEDIA_QUERY.phoneOnly} {
            margin-bottom: ${getSpacingToken('200')}px;
        }

        ${MEDIA_QUERY.largePhoneUp} {
            margin-right: ${getSpacingToken('200')}px;
        }
    }

    ${MEDIA_QUERY.phoneOnly} {
        width: 100%;
    }
`;

export const DialogActionsElement = styled.div`
    padding: ${getSpacingToken('300')}px ${getSpacingToken('300')}px 0;
    text-align: right;
`;

export const ActionsButtonsStack = styled(Stack)`
    ${MEDIA_QUERY.phoneOnly} {
        width: 100%;
    }
`;

export const DialogFooterWithDetail = styled.div`
    padding: ${getSpacingToken('300')}px ${getSpacingToken('300')}px 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    & > * {
        &:not(:first-child) {
            ${MEDIA_QUERY.largePhoneUp} {
                margin-left: ${getSpacingToken('100')}px;
            }
        }
    }

    ${MEDIA_QUERY.phoneOnly} {
        flex-direction: column;
    }
`;

export const FooterDetailContainer = styled.div`
    ${MEDIA_QUERY.phoneOnly} {
        margin-bottom: ${getSpacingToken('200')}px;
    }
`;
